// Shared retry policy + cache TTL constants for the apiFetch wrappers
// in apps/web/src/api.ts and apps/mobile/src/api/index.ts. Lifted in
// 1.34.0 so the policy stays in sync between the two surfaces — a
// drift in MAX_RETRIES or BASE_BACKOFF_MS would be hard to notice
// from a single-surface reading and could subtly change retry
// economics under throttling spikes.
//
// Why these values (per the original WEB-6 commit, 0.67.0):
//
//   * RETRYABLE_ANY_METHOD = {429}. API GW throttle is always
//     pre-execution — the request never reaches Lambda, retrying is
//     safe regardless of method.
//
//   * RETRYABLE_GET_ONLY = {429, 503}. 503 (Lambda concurrency
//     saturation, internal errors mapped down) is usually
//     pre-execution, but a server-side timeout-after-write would
//     surface here too. Retrying a non-GET could double-apply the
//     side effect. Cap 503 retries to GETs; non-GETs bubble up
//     immediately, and the Idempotency-Key header on every non-GET
//     means user-driven retries land at the server's idempotency
//     middleware (deduped via DDB cache).
//
//   * MAX_RETRIES = 4 + BASE_BACKOFF_MS = 250. Worst-case backoff
//     arc is 250 + 500 + 1000 + 2000 = ~15s with the random ±100ms
//     jitter, which is enough to ride out a saturation spike on a
//     hot DDB partition without burning the user's patience.

export const RETRYABLE_ANY_METHOD: ReadonlySet<number> = new Set([429]);
export const RETRYABLE_GET_ONLY: ReadonlySet<number> = new Set([429, 503]);
export const MAX_RETRIES = 4;
export const BASE_BACKOFF_MS = 250;

// Cache TTL constants shared across the two MaxesContext implementations
// (web + mobile) and useMaxBumpCandidates. Lifted alongside the retry
// policy in 1.34.0 — same drift concern: a 24h MAX-bump expiry and a
// 60s maxes cache should evolve at one site, not two.
//
// MAXES_CACHE_TTL_MS = 60_000: MaxesContext re-fetches `/api/me/maxes`
// when the in-memory snapshot is older than 60s. Short enough that a
// user switching tabs and editing a MAX sees a fresh value back in the
// logger; long enough that bouncing between PlanScreen and LoggerScreen
// in the same session doesn't burn requests.
//
// PENDING_MAX_BUMP_TTL_MS = 86_400_000: the post-save MAX-bump candidate
// pill stays accept-able for 24h. After that the candidate is purged
// from `pendingMaxBumps` storage so a stale suggestion can't fire on
// the next morning's session if the user closed the app without
// confirming.
export const MAXES_CACHE_TTL_MS = 60_000;
// ⛔ Deliberately NOT written as DAY_MS, though the value is identical
// (CN-31d(b), closed 2026-07-31). This is a PRODUCT POLICY that currently
// happens to be worth one day — "how long may a suggestion still be
// accepted" — not a day expressed as a duration. Collapsing it onto the
// calendar constant would tie the policy to it, so a later decision to make
// the pill expire in 12h or 48h would read as an edit to day-math rather
// than the policy change it is. The literal is the honest spelling here.
export const PENDING_MAX_BUMP_TTL_MS = 24 * 60 * 60 * 1000;
